import { FaFileInvoice } from "react-icons/fa";
import tw from "tailwind-styled-components";
import { MdOutlineChevronRight } from "react-icons/md";
import Tooltip from "../Tooltip";
import { getDueLabel } from "../../helpers";

function SummaryCards({ summaryData, onClick, setSelectedId, selectedId }) {
    const utilityBreakdown = summaryData?.utilityBreakdown ?? {};
    const otherBreakdown = summaryData?.otherBreakdown ?? {};

    const hasUtilityBreakdown = Object.values(utilityBreakdown).some(
        (v) => Number(v) > 0
    );
    const hasOtherBreakdown = Object.values(otherBreakdown).some(
        (v) => Number(v) > 0
    );

    const currentDueData = [
        {
            amount: "₹" + (summaryData?.totalDues || 0),
            label: "Total Dues",
            color: "text-black",
            icon: <FaFileInvoice className="text-red-400" />,
            filter: "TD",
        },
        {
            amount: "₹" + (summaryData?.rentDues || 0),
            label: "Rent Dues",
            color: "text-black",
            icon: <FaFileInvoice className="text-red-400" />,
            filter: "CRD",
        },
        {
            amount: "₹" + (summaryData?.utilityDues || 0),
            label: "Utility Dues",
            color: "text-black",
            icon: <FaFileInvoice className="text-red-400" />,
            filter: "CUD",
            breakdown: utilityBreakdown,
            breakdownTotal:
                summaryData?.utilityDues ??
                summaryData?.utilitySubTotal ??
                0,
            breakdownTitle: "Utility Dues Breakdown",
            showBreakdown: hasUtilityBreakdown,
        },
        {
            amount: "₹" + (summaryData?.otherDueAmt || 0),
            label: "Other Dues",
            color: "text-black",
            subLabel: "Fine Due",
            subTitle: summaryData?.fineDuesAmt || "0",
            icon: <FaFileInvoice className="text-red-400" />,
            filter: "COD",
            breakdown: otherBreakdown,
            breakdownTotal:
                summaryData?.otherDueAmt ??
                summaryData?.otherSubTotal ??
                0,
            breakdownTitle: "Other Dues Breakdown",
            showBreakdown: hasOtherBreakdown,
        },
        {
            amount: "₹" + (summaryData?.securityShortFall || 0),
            label: "Security Shortfall",
            color: "text-black",
            icon: <FaFileInvoice className="text-red-400" />,
            filter: "SSD",
        },
    ];

    return (
        <Wrapper>
            <ScrollCard>
                {currentDueData.map((item) => {
                    const selected = selectedId === item.filter;

                    return (
                        <Card
                            key={item.filter}
                            $selected={selected}
                            onClick={() => {
                                const isSelected =
                                    selectedId === item.filter;
                                setSelectedId(
                                    isSelected ? null : item.filter
                                );
                                onClick(isSelected ? "All" : item.filter);
                            }}
                        >
                            <ValueRow>
                                <div className="flex flex-row items-baseline">
                                    <Value color={item.color}>
                                        {item.amount}
                                    </Value>
                                    {item.showBreakdown && (
                                        <Tooltip
                                            label={
                                                <BreakdownTooltip
                                                    title={item.breakdownTitle}
                                                    breakdown={item.breakdown}
                                                    total={item.breakdownTotal}
                                                />
                                            }
                                        >
                                            <span className="ml-1 items-baseline text-xs text-gray-400">
                                                ⓘ
                                            </span>
                                        </Tooltip>
                                    )}
                                    {/* {item.subTitle && (
                                        <Tooltip label={item.subLabel}>
                                            <>
                                                <span className="ml-1 text-basline text-sm text-gray-600">
                                                    (₹{item.subTitle})
                                                </span>
                                            </>
                                        </Tooltip>
                                    )} */}


                                </div>
                            </ValueRow>

                            <Label>
                                {item.label}
                                <ArrowWrap className="arrow">
                                    <Chevron />
                                </ArrowWrap>
                            </Label>
                        </Card>
                    );
                })}
            </ScrollCard>
        </Wrapper>
    );
}

function BreakdownTooltip({ title, breakdown, total }) {
    const entries = Object.entries(breakdown)
        .filter(([, v]) => Number(v) > 0)
        .map(([key, amount]) => [
            getDueLabel(Number(key)),
            Number(amount),
        ])
        .sort((a, b) => b[1] - a[1]);

    return (
        <div className="-m-3 w-72 rounded-xl bg-white p-4">
            <div className="mb-3 text-base font-semibold text-gray-800">
                {title}
            </div>

            <div className="flex justify-between gap-6 text-sm">
                <span className="text-gray-600">Total</span>
                <span className="font-semibold text-gray-800">
                    ₹{Number(total || 0).toLocaleString("en-IN")}
                </span>
            </div>

            {entries.length > 0 && (
                <>
                    <div className="my-3 border-t border-gray-200" />

                    <div className="mb-2 text-base font-semibold text-gray-800">
                        By Type
                    </div>

                    <div className="max-h-96 space-y-2 overflow-y-auto pr-1">
                        {entries.map(([label, amount], i) => (
                            <div
                                key={`${label}-${i}`}
                                className="flex justify-between gap-6 text-sm"
                            >
                                <span className="truncate text-gray-600">
                                    {label}
                                </span>
                                <span className="shrink-0 font-medium text-gray-800">
                                    ₹{amount.toLocaleString("en-IN")}
                                </span>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}

const Wrapper = tw.div`w-full`;
const ScrollCard = tw.div`w-full overflow-x-auto flex space-x-4 py-1`;
const Card = tw.div`group min-w-[200px] rounded-lg bg-white p-4 cursor-pointer border border-gray-100 shadow-sm transition-all duration-200 hover:shadow-md hover:border-gray-200 ${({ $selected }) => $selected ? "border-primary-color shadow-md" : ""}`;
const ValueRow = tw.div`flex items-baseline gap-1`;
const Value = tw.div`text-xl font-semibold text-gray-900`;
const Label = tw.div`flex mt-2 text-sm text-gray-500 items-center justify-between`;
const ArrowWrap = tw.div`flex items-center text-gray-300 transition-all duration-200`;
const Chevron = tw(MdOutlineChevronRight)`text-lg transform translate-x-0 opacity-60 group-hover:opacity-100 group-hover:text-gray-500 group-hover:translate-x-1`;

export default SummaryCards;